import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from "@angular/cdk/drag-drop";
import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { finalize } from "rxjs/operators";
import { AuthService } from "src/app/security/auth.service";
import { GrupoMenu, HojaMenu, MenuEstructura } from "src/app/shared/model/security/menu-estructura.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { PlataformaService } from "../plataforma.service";

/** Un grupo en pantalla: el del servidor más un id estable para su lista de arrastre. */
interface GrupoEnPantalla extends GrupoMenu {
  uid: string;
}

interface GrupoNuevo {
  nombre: string;
  icono: string;
  sitio: string;
  fijo: boolean;
}

/**
 * Consola Solser › Menú del sistema.
 *
 * El menú se arma aquí y se guarda completo: arrastre las opciones entre
 * grupos y dentro del grupo, arrastre los grupos para ordenarlos o fíjelos
 * abajo, renombre, cambie iconos, cree o quite grupos. Nada se escribe
 * hasta Guardar; Descartar vuelve a lo que hay en el servidor.
 *
 * Lo que NO se edita: el factory de una opción (es la identidad con la que
 * el servidor autoriza) ni su sitio (quién la ve). Por eso se muestran en
 * gris: sirven para reconocer la opción, no para tocarla.
 */
@Component({
  selector: "app-gestion-menu",
  standalone: true,
  templateUrl: "./gestion-menu.component.html",
  styleUrls: ["./gestion-menu.component.css"],
  imports: [CommonModule, FormsModule, DragDropModule, MatButtonModule],
})
export class GestionMenuComponent implements OnInit {
  cargando = true;
  guardando = false;
  error: string | null = null;

  sitios: string[] = [];
  principales: GrupoEnPantalla[] = [];
  fijos: GrupoEnPantalla[] = [];
  /** Hojas cuyo padre ya no existe. Se pueden sacar; no se puede soltar nada aquí. */
  sinGrupo: GrupoEnPantalla | null = null;

  /** Grupo u hoja con los campos de nombre/icono abiertos. */
  edicion: GrupoMenu | HojaMenu | null = null;
  nuevo: GrupoNuevo | null = null;

  private firmaOriginal = "";
  private consecutivo = 0;

  /** El nombre del grupo nuevo; recibe el foco al abrir el formulario (antes lo pedía el atributo autofocus). */
  @ViewChild("nombreGrupo") private nombreGrupo?: ElementRef<HTMLInputElement>;

  constructor(
    private plataformaService: PlataformaService,
    private authService: AuthService,
    private msg: Msg,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.consultar();
  }

  /** Si hay cambios sin guardar, el navegador pregunta antes de cerrar o recargar. */
  @HostListener("window:beforeunload", ["$event"])
  antesDeSalir(evento: BeforeUnloadEvent): void {
    if (this.hayCambios) {
      evento.preventDefault();
    }
  }

  // ------------------------------------------------------------ carga

  consultar(): void {
    this.cargando = true;
    this.error = null;
    this.plataformaService
      .consultarMenu()
      .pipe(finalize(() => {
        this.cargando = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (estructura) => this.pintar(estructura),
        error: (err) => {
          this.error = err?.message || "No se pudo consultar el menú.";
        },
      });
  }

  private pintar(estructura: MenuEstructura): void {
    this.sitios = estructura?.sitios || [];
    this.principales = [];
    this.fijos = [];
    this.sinGrupo = null;
    this.edicion = null;
    this.nuevo = null;
    for (const grupo of estructura?.grupos || []) {
      const enPantalla: GrupoEnPantalla = { ...grupo, hojas: [...(grupo.hojas || [])], uid: this.uid() };
      if (enPantalla.virtual) {
        this.sinGrupo = enPantalla;
      } else if (this.esFijo(enPantalla)) {
        this.fijos.push(enPantalla);
      } else {
        this.principales.push(enPantalla);
      }
    }
    this.firmaOriginal = this.firma();
  }

  // --------------------------------------------------------- arrastre

  get idsListasDeHojas(): string[] {
    return [...this.principales, ...this.fijos].map((grupo) => "hojas-" + grupo.uid);
  }

  /** «Sin grupo» sólo suelta: soltar ahí sería volver a dejar la opción huérfana. */
  nuncaRecibe = (): boolean => false;

  soltarHoja(evento: CdkDragDrop<HojaMenu[]>): void {
    if (evento.previousContainer === evento.container) {
      moveItemInArray(evento.container.data, evento.previousIndex, evento.currentIndex);
    } else {
      transferArrayItem(evento.previousContainer.data, evento.container.data, evento.previousIndex, evento.currentIndex);
    }
    this.limpiarSinGrupo();
  }

  soltarGrupo(evento: CdkDragDrop<GrupoEnPantalla[]>): void {
    if (evento.previousContainer === evento.container) {
      moveItemInArray(evento.container.data, evento.previousIndex, evento.currentIndex);
      return;
    }
    transferArrayItem(evento.previousContainer.data, evento.container.data, evento.previousIndex, evento.currentIndex);
    const grupo = evento.container.data[evento.currentIndex];
    grupo.seccion = evento.container.data === this.fijos ? "fijo" : null;
  }

  // ------------------------------------------------------------ edición

  esFijo(grupo: GrupoMenu): boolean {
    return (grupo?.seccion || "").toLowerCase() === "fijo";
  }

  alternarFijo(grupo: GrupoEnPantalla): void {
    const origen = this.esFijo(grupo) ? this.fijos : this.principales;
    const destino = origen === this.fijos ? this.principales : this.fijos;
    const indice = origen.indexOf(grupo);
    if (indice < 0) {
      return;
    }
    origen.splice(indice, 1);
    destino.push(grupo);
    grupo.seccion = destino === this.fijos ? "fijo" : null;
  }

  editar(objetivo: GrupoMenu | HojaMenu): void {
    this.edicion = this.edicion === objetivo ? null : objetivo;
  }

  terminarEdicion(): void {
    if (this.edicion && !(this.edicion.nombre || "").trim()) {
      return; // sin nombre no se cierra
    }
    this.edicion = null;
  }

  enEdicion(objetivo: GrupoMenu | HojaMenu): boolean {
    return this.edicion === objetivo;
  }

  abrirNuevo(): void {
    this.nuevo = { nombre: "", icono: "folder", sitio: this.sitios[0] || "COS", fijo: false };
    // El campo aparece hasta el siguiente ciclo de detección de cambios. Se enfoca por código y no con el
    // atributo autofocus: el navegador solo lo atiende una vez por página y Sonar lo marca (Web:S9379).
    setTimeout(() => this.nombreGrupo?.nativeElement.focus());
  }

  crearGrupo(): void {
    if (!this.nuevo || !this.nuevo.nombre.trim()) {
      return;
    }
    const grupo: GrupoEnPantalla = {
      ...new GrupoMenu(),
      nombre: this.nuevo.nombre.trim(),
      icono: this.nuevo.icono.trim() || "folder",
      sitio: this.nuevo.sitio,
      sitios: [this.nuevo.sitio],
      seccion: this.nuevo.fijo ? "fijo" : null,
      tipo: "sub",
      hojas: [],
      uid: this.uid(),
    };
    (this.nuevo.fijo ? this.fijos : this.principales).push(grupo);
    this.nuevo = null;
  }

  quitarGrupo(grupo: GrupoEnPantalla): void {
    if (grupo.hojas.length) {
      return; // sólo se quita un grupo vacío: primero mueva sus opciones
    }
    for (const lista of [this.principales, this.fijos]) {
      const indice = lista.indexOf(grupo);
      if (indice >= 0) {
        lista.splice(indice, 1);
      }
    }
  }

  // ------------------------------------------------------------ guardar

  get hayCambios(): boolean {
    return !this.cargando && this.firma() !== this.firmaOriginal;
  }

  get totalHojas(): number {
    return [...this.principales, ...this.fijos].reduce((suma, grupo) => suma + grupo.hojas.length, 0);
  }

  get gruposSinNombre(): boolean {
    return [...this.principales, ...this.fijos].some((grupo) => !(grupo.nombre || "").trim());
  }

  guardar(): void {
    if (!this.hayCambios || this.guardando || this.gruposSinNombre) {
      return;
    }
    this.guardando = true;
    this.plataformaService
      .guardarMenu(this.estructuraParaGuardar())
      .pipe(finalize(() => {
        this.guardando = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (estructura) => {
          this.pintar(estructura);
          // El propio sidebar se repinta; los demás lo verán al volver a entrar.
          this.authService.refrescarMenus().subscribe({ error: () => undefined });
          this.msg.successCall("Cada usuario lo verá al volver a entrar; el suyo ya se actualizó.", "Menú guardado", null);
        },
        error: (err) => {
          this.msg.errorCall(err?.message || "No se pudo guardar el menú.", "No se guardó", null);
        },
      });
  }

  descartar(): void {
    if (!this.hayCambios) {
      return;
    }
    this.msg.questionCall("Se pierden los cambios que no ha guardado.", "¿Descartar?", (resultado: any) => {
      if (resultado?.isConfirmed) {
        this.consultar();
      }
    });
  }

  // ------------------------------------------------------------ apoyo

  etiquetaPerfiles(hoja: HojaMenu): string {
    const n = hoja.perfiles || 0;
    return n === 0 ? "nadie la tiene" : n === 1 ? "1 perfil" : `${n} perfiles`;
  }

  rastro(_indice: number, grupo: GrupoEnPantalla): string {
    return grupo.uid;
  }

  rastroHoja(_indice: number, hoja: HojaMenu): number {
    return hoja.id;
  }

  /** Sólo los campos del contrato, en el orden en pantalla: principales y luego fijos. */
  private estructuraParaGuardar(): MenuEstructura {
    const estructura = new MenuEstructura();
    const todos: GrupoEnPantalla[] = [...this.principales, ...this.fijos];
    if (this.sinGrupo) {
      todos.push(this.sinGrupo);
    }
    estructura.grupos = todos.map((grupo, indice) => ({
      clave: grupo.clave,
      nombre: (grupo.nombre || "").trim(),
      icono: (grupo.icono || "").trim() || null,
      seccion: this.fijos.includes(grupo) ? "fijo" : null,
      orden: indice,
      tipo: grupo.tipo,
      sitio: grupo.sitio,
      sitios: grupo.sitios || [],
      ids: grupo.ids || [],
      virtual: !!grupo.virtual,
      hojas: (grupo.hojas || []).map((hoja, posicion) => ({
        id: hoja.id,
        nombre: (hoja.nombre || "").trim(),
        icono: (hoja.icono || "").trim() || null,
        factory: hoja.factory,
        sitio: hoja.sitio,
        ruta: hoja.ruta,
        tipo: hoja.tipo,
        orden: posicion,
        perfiles: hoja.perfiles,
      })),
    }));
    estructura.sitios = this.sitios;
    return estructura;
  }

  private firma(): string {
    return JSON.stringify(this.estructuraParaGuardar().grupos);
  }

  /** Cuando «Sin grupo» se vacía, desaparece. */
  private limpiarSinGrupo(): void {
    if (this.sinGrupo && this.sinGrupo.hojas.length === 0) {
      this.sinGrupo = null;
    }
  }

  private uid(): string {
    this.consecutivo += 1;
    return `g${this.consecutivo}`;
  }
}
